import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type {
  FlussonicHlsVideoPlayerSettings,
  FlussonicHlsVideoPlayerViewSettings,
} from '@repo/hls-flussonic-video-player';
import { action, type IReactionDisposer, makeAutoObservable, reaction, runInAction, set } from 'mobx';
import { throttle } from 'throttle-debounce';

import { CurvesCacheService } from '../../shared/api/CurvesCacheService';
import { DashboardService } from '../../shared/api/DashboardService';
import type { VideoBroadcast, Wellbore } from '../../shared/api/IDashboardService';
import { WellDirectoryService } from '../../shared/api/WellDirectoryService';
import { BaseParameterTypes } from '../../shared/baseParams';
import type { SocketCurveMessage, WellLogWidgetStompPublisher } from '../../shared/stompTypes';
import { TimeZonesStore } from '../../shared/stores/TimeZonesStore';
import { Wellbores } from '../../shared/stores/WellboresStore';
import { WellLogs } from '../../shared/stores/WellLogs';
import { roundNumber } from '../../shared/utils';
import type {
  AdditionalInfo,
  TAspectRatio,
  VideoBroadcastAppParams,
  VideoBroadcastEvents,
  VideoBroadcastInitParams,
  VideoBroadcastInterval,
  VideoBroadcastPlaybackMode,
  VideoBroadcastPlaybackTimeSource,
  VideoBroadcastStateParams,
  WellIndexType,
} from '../types';

import { VideoPlaybackSyncStore } from './VideoPlaybackSyncStore';
import { VideoBroadcastStatePublisher } from './VideoBroadcastStatePublisher';

const WELL_DIRECTORY_SERVICE_API_URL = '/well-directory-service/api/v2';

export type { VideoBroadcastInterval } from '../types';

const DEPTH_LAST_VALUE_FRACTION = 2;

const roundDepthLastValue = (value: unknown): number => roundNumber(Number(value), DEPTH_LAST_VALUE_FRACTION, 'round');

type PlayerSettingsUpdateOptions = {
  immediate?: boolean;
};

export class CameraState {
  id: number;

  selected: boolean = false;

  liveAvailable: boolean | null = null;

  liveEdgeMs: number | null = null;

  data: VideoBroadcast;

  constructor(data: VideoBroadcast) {
    this.id = data.id;
    this.data = data;
    makeAutoObservable(this, {
      setSelected: action.bound,
      setLiveStatus: action.bound,
    });
  }

  setSelected(value: boolean) {
    this.selected = value;
  }

  setLiveStatus({ liveAvailable, liveEdgeMs }: { liveAvailable: boolean | null; liveEdgeMs: number | null }) {
    this.liveAvailable = liveAvailable;
    this.liveEdgeMs = liveEdgeMs;
  }
}

export class VideoBroadcastWidgetState {
  wellId: number;

  index: WellIndexType;

  wellbores: Wellbores;

  private logs: WellLogs;

  appParams: VideoBroadcastAppParams;

  timeZonesStore: TimeZonesStore;

  playAll: boolean;

  playerSettingsByCameraId: Record<string, FlussonicHlsVideoPlayerSettings>;

  aspectRatio: TAspectRatio;

  additionalInfo: AdditionalInfo;

  isCamerasLoading: boolean = false;

  cameras: CameraState[] = [];

  visibleCameraIds: number[] = [];

  throttledInit: () => void;

  eventHandlers: VideoBroadcastEvents;

  statePublisher: VideoBroadcastStatePublisher;

  videoPlaybackSync: VideoPlaybackSyncStore<CameraState>;

  stompPublisher: WellLogWidgetStompPublisher;

  curvesCacheService: CurvesCacheService;

  wellDirectoryService: WellDirectoryService;

  dashboardService: DashboardService;

  depthHoleCurveId: number | null = null;

  depthHoleLastValue: number | null = null;

  depthBitCurveId: number | null = null;

  depthBitLastValue: number | null = null;

  operationsCurveId: number | null = null;

  operationsLastValue: string | null | unknown = null;

  kpiCurveId: number | null = null;

  kpiLastValue: string | null | unknown = null;

  subscribeHandlerKpi: (value: SocketCurveMessage) => void;

  subscribeHandlerDepthHole: (value: SocketCurveMessage) => void;

  subscribeHandlerDepthBit: (value: SocketCurveMessage) => void;

  subscribeHandlerOperations: (value: SocketCurveMessage) => void;

  abortController: AbortController | null = null;

  wellboresLoading: boolean = false;

  isBaseParamsLoading: boolean = false;

  isCameraListOpen: boolean = false;

  private order: number[];

  private selected: number[];

  disposerUnsub: IReactionDisposer;

  unsubTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    initParams: VideoBroadcastInitParams,
    appParams: VideoBroadcastAppParams,
    initState: Partial<VideoBroadcastStateParams>,
    eventHandlers: VideoBroadcastEvents,
  ) {
    this.wellId = appParams.wellId;
    this.appParams = appParams;
    this.index = initParams.index;
    this.eventHandlers = eventHandlers;
    this.statePublisher = new VideoBroadcastStatePublisher(initState, this.eventHandlers?.onInternalStateChanged);
    this.curvesCacheService = new CurvesCacheService(initParams.legacyAxiosInstance);
    this.wellDirectoryService = new WellDirectoryService(initParams.axiosInstance, WELL_DIRECTORY_SERVICE_API_URL);
    this.dashboardService = new DashboardService(initParams.axiosInstance);
    this.wellbores = new Wellbores(this.dashboardService, (wellbore) => {
      if (this.eventHandlers.onWellboreChanged) {
        this.eventHandlers.onWellboreChanged(wellbore.specialName);
      }
    });
    this.stompPublisher = initParams.stompPublisher;
    this.logs = new WellLogs(this.wellDirectoryService, initParams.wellDirectoryServiceStompPublisher);
    this.timeZonesStore = new TimeZonesStore(this.statePublisher.params.timeZone ?? 'user', (value) => {
      this.statePublisher.setParams({ timeZone: value });
    });

    this.additionalInfo = initState.additionalInfo || {
      camName: false,
      operations: false,
      depthHole: false,
      depthBit: false,
      kpi: false,
    };
    this.aspectRatio = initState.aspectRatio || '1x1';
    this.playAll = initState.playAll || false;
    this.playerSettingsByCameraId = initState.playerSettingsByCameraId || {};
    this.videoPlaybackSync = new VideoPlaybackSyncStore<CameraState>({
      eventHandlers: this.eventHandlers,
      getCameraSettingsKey: (camera) => this.getCameraSettingsKey(camera),
      getPlayerSettingsByCameraId: () => this.playerSettingsByCameraId,
      getSelectedCameras: () => this.selectedCameras,
      getVisibleCameraKeys: () => this.visibleCameras.map((camera) => this.getCameraSettingsKey(camera)),
      setPlayerSettingsByCameraId: (settings) => {
        this.playerSettingsByCameraId = settings;
      },
      statePublisher: this.statePublisher,
    });
    this.statePublisher.replaceParams({
      playbackMode: this.videoPlaybackSync.playbackMode,
      playbackTimeMs: this.videoPlaybackSync.playbackTimeMs,
    });
    this.order = initState.camerasOrder || [];
    this.selected = initState.selectedCameras || [];
    this.throttledInit = throttle(800, this.init);

    this.init();

    this.subscribeHandlerDepthHole = (value: SocketCurveMessage) => {
      switch (value.msgType) {
        case 'POINT':
          runInAction(() => {
            this.depthHoleLastValue = roundDepthLastValue(value.value);
          });
          break;
        default:
          break;
      }
    };

    this.subscribeHandlerDepthBit = (value: SocketCurveMessage) => {
      switch (value.msgType) {
        case 'POINT':
          runInAction(() => {
            this.depthBitLastValue = roundDepthLastValue(value.value);
          });
          break;
        default:
          break;
      }
    };

    this.subscribeHandlerOperations = (value: SocketCurveMessage) => {
      switch (value.msgType) {
        case 'POINT':
          runInAction(() => {
            this.operationsLastValue = value.value;
          });
          break;
        default:
          break;
      }
    };

    this.subscribeHandlerKpi = (value: SocketCurveMessage) => {
      switch (value.msgType) {
        case 'POINT':
          runInAction(() => {
            this.kpiLastValue = value.value;
          });
          break;
        default:
          break;
      }
    };

    this.disposerUnsub = reaction(
      () => this.appParams.active,
      (active) => {
        if (!active) {
          this.clearSubTimeout();
          this.unsubTimeout = setTimeout(() => {
            this.unsubscribeSocket();
            this.clearSubTimeout();
          }, 30 * 1000);
        } else {
          this.subscribeSocket();
          this.clearSubTimeout();
        }
      },
    );

    makeAutoObservable(this, {
      setVisibleCameras: action.bound,
      updateSelectedCams: action.bound,
    });
  }

  async init() {
    runInAction(() => {
      this.wellboresLoading = true;
    });
    try {
      await this.setWell(this.wellId);

      if (this.eventHandlers.onInitializedSuccess) {
        this.eventHandlers.onInitializedSuccess();
      }
    } catch (e: unknown) {
      if (this.eventHandlers.onInitializationFailed) {
        runInAction(() => {
          this.wellboresLoading = false;
        });
        if (e instanceof Error) {
          this.eventHandlers.onInitializationFailed(e.message, e);
        } else {
          this.eventHandlers.onInitializationFailed('Unexpected error');
        }
      }
    } finally {
      runInAction(() => {
        this.wellboresLoading = false;
      });
    }
  }
  async setWell(wellId: number) {
    try {
      runInAction(() => {
        this.isCamerasLoading = true;
      });

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();
      this.wellId = wellId;
      const abortController = this.abortController;

      this.wellDirectoryService
        .getWell(wellId, abortController)
        .then((well) => {
          runInAction(() => {
            this.timeZonesStore.setWellTimeZoneByString(well.timeZone);
          });
        })
        .catch((e) => {
          if (!abortController.signal.aborted) {
            console.error(e);
          }
        });

      const cams = await this.dashboardService.getVideoBroadcast(this.wellId, abortController);
      this.setCameras(cams);

      const wellbores = await this.wellbores.fetchWellbores(wellId, abortController);
      if (wellbores[0]) {
        const selected = this.wellbores.getDefaultSelected(
          this.appParams.defaultSelectWellbore === 'active',
          this.statePublisher.params.wellboreId,
        );
        if (selected) {
          await this.setWellbore(selected);
        }
      }
    } catch (e) {
      console.error('Well not found', e);
    } finally {
      runInAction(() => {
        this.isCamerasLoading = false;
      });
    }
  }
  async setWellbore(wellbore: Wellbore) {
    this.unsubscribeSocket();
    this.wellbores.setSelected(wellbore);
    await this.setBaseParameters(wellbore.id);
    this.subscribeSocket();
    await this.logs.fetchWellLogs(this.wellId, wellbore.id, this.index);
  }
  async switchWellbore(wellboreId: number) {
    try {
      const bore = this.wellbores.list.find((obj) => obj.id === wellboreId);
      if (bore) {
        await this.setWellbore(bore);
        this.statePublisher.setParams({
          wellboreId: this.wellbores.selected?.id,
        });
      }
    } catch (e) {
      console.error('Wellbore not found.', e);
    }
  }

  async setBaseParameters(wellboreId: number) {
    try {
      runInAction(() => {
        this.isBaseParamsLoading = true;
      });
      const baseParams = await this.wellDirectoryService.getBaseParameters(wellboreId, this.index);
      const depthHoleParam = baseParams.find((param) => param.parameter === BaseParameterTypes.DEPTH_HOLE);
      const depthBitParam = baseParams.find((param) => param.parameter === BaseParameterTypes.DEPTH_BIT);
      const kpiParam = baseParams.find((param) => param.parameter === BaseParameterTypes.AUTO_RECOGNIZED_EVENTS);

      const operationsParam = baseParams.find(
        (param) => param.parameter === BaseParameterTypes.AUTO_RECOGNIZED_OPERATIONS,
      );
      if (depthHoleParam && depthHoleParam.curveId) {
        const data = await this.curvesCacheService.getCurve(depthHoleParam.curveId);
        runInAction(() => {
          if (depthHoleParam.curveId) this.depthHoleCurveId = depthHoleParam.curveId;

          this.depthHoleLastValue = roundDepthLastValue(data.lastValue);
        });
      } else {
        runInAction(() => {
          this.depthHoleCurveId = null;

          this.depthHoleLastValue = null;
        });
      }

      if (depthBitParam && depthBitParam.curveId) {
        const data = await this.curvesCacheService.getCurve(depthBitParam.curveId);
        runInAction(() => {
          if (depthBitParam.curveId) this.depthBitCurveId = depthBitParam.curveId;

          this.depthBitLastValue = roundDepthLastValue(data.lastValue);
        });
      } else {
        runInAction(() => {
          this.depthBitCurveId = null;

          this.depthBitLastValue = null;
        });
      }

      if (kpiParam && kpiParam.curveId) {
        const data = await this.curvesCacheService.getCurve(kpiParam.curveId);

        runInAction(() => {
          if (kpiParam.curveId) this.kpiCurveId = kpiParam.curveId;

          this.kpiLastValue = data.lastValue;
        });
      } else {
        runInAction(() => {
          this.kpiCurveId = null;

          this.kpiLastValue = null;
        });
      }

      if (operationsParam && operationsParam.curveId) {
        const data = await this.curvesCacheService.getCurve(operationsParam.curveId);

        runInAction(() => {
          if (operationsParam.curveId) this.operationsCurveId = operationsParam.curveId;

          this.operationsLastValue = data.lastValue;
        });
      } else {
        runInAction(() => {
          this.operationsCurveId = null;

          this.operationsLastValue = null;
        });
      }
    } catch (e) {
      console.error('ErrorToFetchBaseParams', e);
    } finally {
      runInAction(() => {
        this.isBaseParamsLoading = false;
      });
    }
  }

  clearSubTimeout() {
    if (this.unsubTimeout) {
      clearTimeout(this.unsubTimeout);
      this.unsubTimeout = null;
    }
  }

  setCameras(cams: VideoBroadcast[]) {
    this.cameras = cams.map((camera) => {
      const camState = new CameraState(camera);
      if (this.selected.length) {
        camState.setSelected(this.selected.includes(camera.cameraNameId));
      }
      return camState;
    });
    if (!this.selectedCameras.length && this.cameras.length) {
      this.cameras[0].setSelected(true);
    }
    if (this.order.length) {
      this.cameras = this.cameras.sort((a, b) => this.order.indexOf(a.id) - this.order.indexOf(b.id));
    }
  }

  subscribeSocket() {
    if (this.operationsCurveId) {
      this.stompPublisher.subscribe(`/curve/${this.operationsCurveId}`, this.subscribeHandlerOperations);
    }
    if (this.kpiCurveId) {
      this.stompPublisher.subscribe(`/curve/${this.kpiCurveId}`, this.subscribeHandlerKpi);
    }
    if (this.depthHoleCurveId) {
      this.stompPublisher.subscribe(`/curve/${this.depthHoleCurveId}`, this.subscribeHandlerDepthHole);
    }
    if (this.depthBitCurveId) {
      this.stompPublisher.subscribe(`/curve/${this.depthBitCurveId}`, this.subscribeHandlerDepthBit);
    }
  }

  unsubscribeSocket() {
    if (this.operationsCurveId) {
      this.stompPublisher.unsubscribe(`/curve/${this.operationsCurveId}`, this.subscribeHandlerOperations);
    }
    if (this.kpiCurveId) {
      this.stompPublisher.unsubscribe(`/curve/${this.kpiCurveId}`, this.subscribeHandlerKpi);
    }
    if (this.depthHoleCurveId) {
      this.stompPublisher.unsubscribe(`/curve/${this.depthHoleCurveId}`, this.subscribeHandlerDepthHole);
    }
    if (this.depthBitCurveId) {
      this.stompPublisher.unsubscribe(`/curve/${this.depthBitCurveId}`, this.subscribeHandlerDepthBit);
    }
  }

  setAppParams(params: Partial<VideoBroadcastAppParams>) {
    if (params.locale && this.wellbores.selected) {
      this.eventHandlers.onWellboreChanged?.(this.wellbores.selected.specialName);
    }
    if (params.wellId != null && params.wellId !== this.wellId) {
      this.wellId = params.wellId;
      this.throttledInit();
    }
    set(this.appParams, params);
  }

  togglePlayAll() {
    this.playAll = !this.playAll;
    this.statePublisher.setParams({
      playAll: this.playAll,
    });
  }

  get playbackMode() {
    return this.videoPlaybackSync.playbackMode;
  }

  get playbackTimeMs() {
    return this.videoPlaybackSync.playbackTimeMs;
  }

  get videoInterval() {
    return this.videoPlaybackSync.videoInterval;
  }

  get playerRemountKey() {
    return this.videoPlaybackSync.playerRemountKey;
  }

  get linkedVideoSyncEnabled() {
    return this.videoPlaybackSync.linkedVideoSyncEnabled;
  }

  seekToTime(timeMs: number) {
    this.videoPlaybackSync.seekToTime(timeMs);
  }

  setExternalPlaybackTime(timeMs: number): void {
    this.videoPlaybackSync.setExternalPlaybackTime(timeMs);
  }

  showVideoInterval(interval: { fromMs: number; toMs: number }) {
    this.videoPlaybackSync.showVideoInterval(interval);
  }

  clearVideoInterval() {
    this.videoPlaybackSync.clearVideoInterval();
  }

  setLinkedVideoSyncEnabled(enabled: boolean, timeMs: number | null = null) {
    this.videoPlaybackSync.setLinkedVideoSyncEnabled(enabled, timeMs);
  }

  seekRelativeToCurrentTime(deltaMs: number) {
    this.videoPlaybackSync.seekRelativeToCurrentTime(deltaMs);
  }

  setLiveMode() {
    this.videoPlaybackSync.setLiveMode();
  }

  get currentPlaybackTimeMs(): number | null {
    return this.videoPlaybackSync.currentPlaybackTimeMs;
  }

  shouldAutoplayCamera() {
    return this.playAll;
  }

  setPlayerPlaybackTime(
    camera: CameraState,
    timeMs: number,
    source: VideoBroadcastPlaybackTimeSource,
    playbackMode: VideoBroadcastPlaybackMode,
  ) {
    this.videoPlaybackSync.setPlayerPlaybackTime(camera, timeMs, source, playbackMode);
  }

  getPlayerSettings(camera: CameraState) {
    return this.videoPlaybackSync.getPlayerSettings(camera);
  }

  getPlayerInitialPlaybackState(camera: CameraState) {
    return this.videoPlaybackSync.getPlayerInitialPlaybackState(camera);
  }

  setPlayerSettings(
    camera: CameraState,
    settings: Partial<FlussonicHlsVideoPlayerViewSettings>,
    options: PlayerSettingsUpdateOptions = {},
  ) {
    this.videoPlaybackSync.setPlayerSettings(camera, settings, options);
  }

  setCameraLiveStatus(
    camera: CameraState,
    status: {
      liveAvailable: boolean | null;
      liveEdgeMs: number | null;
    },
  ) {
    this.videoPlaybackSync.setCameraLiveStatus(camera, status);
  }

  confirmPlayerArchiveUnavailable(camera: CameraState, timeMs: number) {
    this.videoPlaybackSync.confirmPlayerArchiveUnavailable(camera, timeMs);
  }

  setAspectRatio(value: TAspectRatio) {
    this.aspectRatio = value;
    this.statePublisher.setParams({
      aspectRatio: value,
    });
  }

  setAdditionalInfo(value: Partial<AdditionalInfo>) {
    this.additionalInfo = { ...this.additionalInfo, ...value };
    this.statePublisher.setParams({
      additionalInfo: { ...this.additionalInfo, ...value },
    });
  }

  setIsCameraListOpen(value: boolean) {
    this.isCameraListOpen = value;
  }

  onDragEnd = ({ active, over }: DragEndEvent) => {
    const oldPosition = active.data.current?.sortable?.index;
    const newPosition = over?.data.current?.sortable?.index;
    if (over) {
      this.moveCamera(oldPosition, newPosition);
    }
  };

  updateSelectedCams() {
    const selectedCameras = this.cameras.reduce<number[]>((acc, cam) => {
      if (cam.selected) {
        acc.push(cam.data.cameraNameId);
      }
      return acc;
    }, []);

    this.statePublisher.setParams({
      selectedCameras,
    });
  }

  setSelectedCams(camsNameIds: number[]) {
    this.selected = camsNameIds;
  }

  private moveCamera(fromPos: number, toPos: number) {
    this.cameras = arrayMove(this.cameras, fromPos, toPos);

    this.statePublisher.setParams({
      camerasOrder: this.cameras.map((cam) => cam.id),
    });
  }

  private get depthHoleLog() {
    return this.logs.allCurves.find((curve) => curve.goStreamId === this.depthHoleCurveId);
  }

  private get depthBitLog() {
    return this.logs.allCurves.find((curve) => curve.goStreamId === this.depthBitCurveId);
  }

  get depthHoleNameAndUom(): { name: string; uom: string } | null {
    if (this.depthHoleLog) {
      return {
        name: 'sidebar.holeDepth',
        uom: this.depthHoleLog.unit.abbreviation,
      };
    }
    return null;
  }

  get depthBitNameAndUom(): { name: string; uom: string } | null {
    if (this.depthBitLog) {
      return {
        name: 'sidebar.bitDepth',
        uom: this.depthBitLog.unit.abbreviation,
      };
    }
    return null;
  }

  get anyCameraSelected() {
    return this.cameras.some((cam) => cam.selected);
  }

  get selectedCamerasQuantity() {
    return this.selectedCameras.length;
  }

  get selectedCameras() {
    return this.cameras.filter((cam) => cam.selected);
  }

  get visibleCameras() {
    const visibleCameraIds = new Set(this.visibleCameraIds);
    return this.selectedCameras.filter((camera) => visibleCameraIds.has(camera.id));
  }

  isCameraVisible(camera: CameraState) {
    return this.visibleCameraIds.includes(camera.id);
  }

  private getCameraSettingsKey(camera: CameraState) {
    return String(camera.data.cameraNameId ?? camera.id);
  }

  setVisibleCameras(cameras: CameraState[]) {
    const visibleCameraIds = cameras.map((camera) => camera.id);
    if (isSameNumberList(this.visibleCameraIds, visibleCameraIds)) {
      return;
    }

    this.visibleCameraIds = visibleCameraIds;
  }

  destroy() {
    this.videoPlaybackSync.destroy();
    this.unsubscribeSocket();
    this.logs.destroy();
  }
}

function isSameNumberList(first: number[], second: number[]) {
  return first.length === second.length && first.every((value, index) => value === second[index]);
}
